import axios from "axios";
import { toast } from "react-toastify";
import { decryptData, encryptData } from "../crypto";
const baseURL = process.env.REACT_APP_API_BASE_URL;

const axios_instance = axios.create({
  baseURL: baseURL,
});

axios_instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const errorMsg = error.response?.data?.message;

    // 🔄 Handle Token Refresh
    if (
      errorMsg === "jwt expired" &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/v1/b2b/client/auth/re_login")
    ) {
      originalRequest._retry = true;
      try {
        const encryptedRefreshToken = sessionStorage.getItem("refresh_token");
        const refreshToken = decryptData(encryptedRefreshToken);

        if (refreshToken) {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/client/auth/re_login`,
            {
              headers: { "x-refreshtoken": refreshToken },
            },
          );

          if (res.data.success) {
            const newToken = res.data.token;
            const newRefreshToken = res.data.refresh_token;

            sessionStorage.setItem("token", encryptData(newToken));
            if (newRefreshToken) {
              sessionStorage.setItem(
                "refresh_token",
                encryptData(newRefreshToken),
              );
            }

            // Update Header and Retry Original Request
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios_instance(originalRequest);
          }
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
      }
    }

    if (
      errorMsg === "Invalid or expired token. Please refresh your session." ||
      errorMsg === "You are logged out. Please log in again." ||
      errorMsg === "Session expired. Please log in again." ||
      errorMsg === "jwt expired" ||
      errorMsg === "No auth token"|| 
      errorMsg === "Invalid or expired token. Please log in again."||
      errorMsg === "invalid token"
    ) {
      window.dispatchEvent(new CustomEvent("session-expired"));
    }
    return Promise.reject(error);
  },
);

export async function getRZPKey() {
  try {
    var response = await axios_instance.get(`v1/api/razorpay/key`);
    console.log("Response:", response);
    return response;
  } catch (err) {
    console.error("Error in getRZPKey:", err);
    if (
      err?.response?.data?.message ===
      "Invalid or expired token. Please refresh your session."
    ) {
      window.location.reload();
      localStorage.clear();
    } else if (
      err?.response?.data?.message ===
      "You are logged out. Please log in again."
    ) {
      window.location.reload();
      localStorage.clear();
    } else if (err?.response?.data?.message) {
      toast.error(err?.response?.data?.message);
    }
  }
}

export const fetchProfileApi = async () => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/auth/profile`,
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );

    return response;
  } catch (error) {
    console.error("API error:", error);

    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export async function UpgradePlan(payload, token) {
  try {
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const response = await axios_instance.post(`v1/b2b/upgradeplan`, payload, {
      headers,
    });
    console.log("UpgradePlan response:", response);
    return response;
  } catch (err) {
    console.error("Error in UpgradePlan:", err);
    if (
      err?.response?.data?.message ===
      "Invalid or expired token. Please refresh your session."
    ) {
      window.location.reload();
      localStorage.clear();
    } else if (
      err?.response?.data?.message ===
      "You are logged out. Please log in again."
    ) {
      window.location.reload();
      localStorage.clear();
    } else if (err?.response?.data?.message) {
      toast.error(err?.response?.data?.message);
    }
  }
}

export async function getSlotListData(decryptedToken) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/centers_slot`,
      {
        headers: {
          Authorization: `Bearer ${decryptData(sessionStorage.getItem("token"))}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching slot list:", error);
    throw error;
  }
}

export const generateLiveSubmitResult = async (examId) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/live/submit/result/generate/all`,
      {
        exam: {
          exam_id: examId,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const fetchQuestionPaperApi = async (examId) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/question/by_exam/${examId}`,
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const fetchExamDate = async () => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/by/date`,
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const fetchExamByDate = async (date, examType = "") => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/one/by/date?start_date=${date}${
      examType ? `&exam_type=${encodeURIComponent(examType)}` : ""
    }`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const fetchExamByDateDailyDashboard = async (date) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/calendar/date?start_date=${date}`,
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const fetchCenterByDate = async (date) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/sync/status/center/${date}`,
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const Report2Api = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report02/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report02/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob",
    //   },
    // );

    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const Report6Api = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report06/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const ReportFacultyFeedbackApi = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/center_feedback_response_report/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
      responseType: "blob",
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data) {
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            return { success: false, message: parsed.message };
          }
        } catch (e) {}
      } else if (error.response.data.message) {
        return { success: false, message: error.response.data.message };
      }
    }
    return {
      success: false,
      message: "An error occurred during the API request.",
    };
  }
};

export const Report10Api = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report06/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob",
    //   },
    // );
    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report10/exam_time_learner/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const Report7Api = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report07/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob",
    //   },
    // );

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report07/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const Report8Api = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/merge/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob", // ⭐ important
    //   },
    // );
    const result = await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/live/submit/result/generate/all`,
      {
        exam: {
          exam_id: id,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    let response;

    if (result.data.success) {
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/merge/${id}`;
      response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
        responseType: "blob",
      });
    }

    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const CenterFeedbackReportApi = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/center_feedback_report/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
      responseType: "blob",
    });

    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data) {
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            return { success: false, message: parsed.message };
          }
        } catch (e) {}
      } else if (error.response.data.message) {
        return { success: false, message: error.response.data.message };
      }
    }
    return {
      success: false,
      message: "An error occurred during the API request.",
    };
  }
};


export const DateWiseReportApi = async (fromDate, toDate) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/date/wise/report`,
      {
        from_date: fromDate,
        to_date: toDate,
      },
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
        responseType: "blob",
      },
    );
    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const DistinctPaperCodeReportApi = async (fromDate, toDate) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/date/wise/same/paper_code/report`,
      {
        from_date: fromDate,
        to_date: toDate,
      },
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
        responseType: "blob",
      },
    );
    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};

export const dashboardApi = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/giving/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};
// export const dashboardApi = async (id) => {
//   // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
//   const decryptedToken = decryptData(sessionStorage.getItem("token"));
//   try {
//     const response = await axios.get(
//       `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/giving/${id}`,
//       {
//         headers: {
//           Authorization: `Bearer ${decryptedToken}`,
//         },
//         responseType: "blob",
//       }
//     );
//     return response;
//   } catch (error) {
//     console.error("API error:", error);
//     // Check if the error has a response with a message
//     if (error.response && error.response.data && error.response.data.message) {
//       return { success: false, message: error.response.data.message };
//     } else {
//       // If no specific error message found, return a generic message
//       return {
//         success: false,
//         message: "An error occurred during the API request.",
//       };
//     }
//   }
// };

export const Report1Api = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};


export const ReportIBTApi = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/merge/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob", // ⭐ important
    //   },
    // );

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/ibt_merge/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
      responseType: "blob",
    });

    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};
export const CorrectIncorrectSectionWiseReportApi = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    const liveResult = await generateLiveSubmitResult(id);
    if (!liveResult?.success) {
      return liveResult;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/new_correct_incorrect_report07/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};
export const VerticalResponseReportCBT = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    // const liveResult = await generateLiveSubmitResult(id);
    // if (!liveResult?.success) {
    //   return liveResult;
    // }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/uid_cbt_report/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
      responseType: "blob",
    });
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report02/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob",
    //   },
    // );

    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data) {
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            return { success: false, message: parsed.message };
          }
        } catch (e) {}
      } else if (error.response.data.message) {
        return { success: false, message: error.response.data.message };
      }
    }
    return {
      success: false,
      message: "An error occurred during the API request.",
    };
  }
};

export const VerticalResponseReportIBT = async (id) => {
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    // const liveResult = await generateLiveSubmitResult(id);
    // if (!liveResult?.success) {
    //   return liveResult;
    // }

    const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/uid_ibt_report/${id}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
      },
      responseType: "blob",
    });
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report02/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob",
    //   },
    // );

    return response;
  } catch (error) {
    console.error("API error:", error);
    if (error.response && error.response.data) {
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            return { success: false, message: parsed.message };
          }
        } catch (e) {}
      } else if (error.response.data.message) {
        return { success: false, message: error.response.data.message };
      }
    }
    return {
      success: false,
      message: "An error occurred during the API request.",
    };
  }
};

export const GolDetailReportApi = async (id) => {
  // const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  try {
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/merge/${id}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${decryptedToken}`,
    //     },
    //     responseType: "blob", // ⭐ important
    //   },
    // );
    const result = await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/live/submit/result/generate/all`,
      {
        exam: {
          exam_id: id,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      },
    );
    let response;

    if (result.data.success) {
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/report_right_wrong/${id}`;
      response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
        responseType: "blob",
      });
    }

    return response;
  } catch (error) {
    console.error("API error:", error);
    // Check if the error has a response with a message
    if (error.response && error.response.data && error.response.data.message) {
      return { success: false, message: error.response.data.message };
    } else {
      // If no specific error message found, return a generic message
      return {
        success: false,
        message: "An error occurred during the API request.",
      };
    }
  }
};
